import React, { useState, useEffect } from 'react';
import axios from 'axios';
import API_URL from '../api';
import { useToast } from '../contexts/ToastContext';
import { Plus, Edit, Trash2, ShoppingBag, Check, X, Upload, Globe, MapPin } from 'lucide-react';
import { resolveAssetUrl } from '../utils/assetUrl';
import './StoreManager.css';

const StoreManager = ({ user }) => {
    const [items, setItems] = useState([]);
    const [purchases, setPurchases] = useState([]);
    const [cities, setCities] = useState([]);
    const [activeTab, setActiveTab] = useState('orders'); // 'orders' or 'inventory'
    const [loading, setLoading] = useState(true);
    
    // Modal states for creating/editing items
    const [isItemModalOpen, setIsItemModalOpen] = useState(false);
    const [editingItem, setEditingItem] = useState(null);
    const [itemForm, setItemForm] = useState({
        title: '',
        description: '',
        price: '',
        stock: -1,
        city: '',
        imageUrl: '',
        isActive: true
    });
    
    const [uploadingImage, setUploadingImage] = useState(false);
    const [submittingForm, setSubmittingForm] = useState(false);
    const [purchaseFilter, setPurchaseFilter] = useState('all'); // 'all', 'pending', 'completed', 'canceled'

    const { success, error } = useToast();

    useEffect(() => {
        fetchData();
        fetchCities();
    }, [activeTab]);

    const fetchData = async () => {
        setLoading(true);
        try {
            const token = localStorage.getItem('token');
            const headers = { Authorization: `Bearer ${token}` };
            
            if (activeTab === 'inventory') {
                const res = await axios.get(`${API_URL}/store/admin/items`, { headers });
                setItems(res.data);
            } else {
                const res = await axios.get(`${API_URL}/store/admin/purchases`, { headers });
                setPurchases(res.data);
            }
        } catch (err) {
            error(err.response?.data?.error || 'Помилка завантаження даних');
        } finally {
            setLoading(false);
        }
    };

    const fetchCities = async () => {
        try {
            const token = localStorage.getItem('token');
            const res = await axios.get(`${API_URL}/cities`, {
                headers: { Authorization: `Bearer ${token}` }
            });
            setCities(res.data);
        } catch (err) {
            console.error('Failed to fetch cities:', err);
        }
    };

    const handleOpenItemModal = (item = null) => {
        if (item) {
            setEditingItem(item);
            setItemForm({
                title: item.title,
                description: item.description || '',
                price: item.price,
                stock: item.stock,
                city: item.city || '',
                imageUrl: item.imageUrl || '',
                isActive: item.isActive
            });
        } else {
            setEditingItem(null);
            setItemForm({
                title: '',
                description: '',
                price: '',
                stock: -1,
                city: '',
                imageUrl: '',
                isActive: true
            });
        }
        setIsItemModalOpen(true);
    };

    const handleCloseItemModal = () => {
        setIsItemModalOpen(false);
        setEditingItem(null);
    };

    const handleFormChange = (e) => {
        const { name, value, type, checked } = e.target;
        setItemForm(prev => ({
            ...prev,
            [name]: type === 'checkbox' ? checked : value
        }));
    };

    const handleImageUpload = async (e) => {
        const file = e.target.files[0];
        if (!file) return;
        
        const formData = new FormData();
        formData.append('file', file);
        
        setUploadingImage(true);
        try {
            const token = localStorage.getItem('token');
            const res = await axios.post(`${API_URL}/upload`, formData, {
                headers: {
                    'Content-Type': 'multipart/form-data',
                    Authorization: `Bearer ${token}`
                }
            });
            setItemForm(prev => ({ ...prev, imageUrl: res.data.url }));
            success('Зображення успішно завантажено');
        } catch (err) {
            error(err.response?.data?.error || 'Помилка завантаження зображення');
        } finally {
            setUploadingImage(false);
        }
    };

    const handleSubmitItemForm = async (e) => {
        e.preventDefault();
        if (!itemForm.title || !itemForm.price) {
            error('Будь ласка, заповніть обов\'язкові поля');
            return;
        }
        
        setSubmittingForm(true);
        try {
            const token = localStorage.getItem('token');
            const headers = { Authorization: `Bearer ${token}` };
            
            if (editingItem) {
                await axios.put(`${API_URL}/store/admin/items/${editingItem._id}`, itemForm, { headers });
                success('Товар оновлено успішно');
            } else {
                await axios.post(`${API_URL}/store/admin/items`, itemForm, { headers });
                success('Товар створено успішно');
            }
            
            handleCloseItemModal();
            fetchData();
        } catch (err) {
            error(err.response?.data?.error || 'Помилка збереження товару');
        } finally {
            setSubmittingForm(false);
        }
    };

    const handleDeleteItem = async (id) => {
        if (!window.confirm('Ви впевнені, що хочете видалити цей товар з бази?')) return;
        
        try {
            const token = localStorage.getItem('token');
            await axios.delete(`${API_URL}/store/admin/items/${id}`, {
                headers: { Authorization: `Bearer ${token}` }
            });
            success('Товар видалено');
            fetchData();
        } catch (err) {
            error(err.response?.data?.error || 'Помилка видалення товару');
        }
    };

    const handleUpdatePurchaseStatus = async (id, status) => {
        const actionText = status === 'completed' ? 'видати товар та закрити замовлення' : 'скасувати замовлення з поверненням монет гравцю';
        if (!window.confirm(`Ви впевнені, що хочете ${actionText}?`)) return;
        
        try {
            const token = localStorage.getItem('token');
            await axios.put(`${API_URL}/store/admin/purchases/${id}`, { status }, {
                headers: { Authorization: `Bearer ${token}` }
            });
            success(status === 'completed' ? 'Замовлення відзначено як виконане' : 'Замовлення скасовано, монети повернуто');
            fetchData();
        } catch (err) {
            error(err.response?.data?.error || 'Помилка оновлення статусу замовлення');
        }
    };

    const filteredPurchases = purchases.filter(pur => {
        if (purchaseFilter === 'all') return true;
        return pur.status === purchaseFilter;
    });

    return (
        <div className="store-admin-container">
            {/* Header Area */}
            <div className="store-admin-header">
                <div>
                    <h1>🏪 Управління магазином</h1>
                    <p>Додавайте нові товари для вітрини та керуйте замовленнями гравців на видачу призів</p>
                </div>
                <div className="store-admin-actions">
                    <button 
                        className={`store-admin-tab-btn ${activeTab === 'orders' ? 'active' : ''}`}
                        onClick={() => setActiveTab('orders')}
                    >
                        <span>Замовлення</span>
                        {purchases.filter(p => p.status === 'pending').length > 0 && (
                            <span className="pending-badge">{purchases.filter(p => p.status === 'pending').length}</span>
                        )}
                    </button>
                    <button 
                        className={`store-admin-tab-btn ${activeTab === 'inventory' ? 'active' : ''}`}
                        onClick={() => setActiveTab('inventory')}
                    >
                        <span>Асортимент товарів</span>
                    </button>
                    {activeTab === 'inventory' && (
                        <button className="btn-add-item" onClick={() => handleOpenItemModal()}>
                            <Plus size={18} />
                            <span>Додати товар</span>
                        </button>
                    )}
                </div>
            </div>

            {/* Sub-header Filter Row for Orders */}
            {activeTab === 'orders' && (
                <div className="orders-filter-row">
                    <button 
                        className={`filter-chip ${purchaseFilter === 'all' ? 'active' : ''}`}
                        onClick={() => setPurchaseFilter('all')}
                    >
                        Всі замовлення ({purchases.length})
                    </button>
                    <button 
                        className={`filter-chip ${purchaseFilter === 'pending' ? 'active' : ''}`}
                        onClick={() => setPurchaseFilter('pending')}
                    >
                        Очікують видачі ({purchases.filter(p => p.status === 'pending').length})
                    </button>
                    <button 
                        className={`filter-chip ${purchaseFilter === 'completed' ? 'active' : ''}`}
                        onClick={() => setPurchaseFilter('completed')}
                    >
                        Видані ({purchases.filter(p => p.status === 'completed').length})
                    </button>
                    <button 
                        className={`filter-chip ${purchaseFilter === 'canceled' ? 'active' : ''}`}
                        onClick={() => setPurchaseFilter('canceled')}
                    >
                        Скасовані ({purchases.filter(p => p.status === 'canceled').length})
                    </button>
                </div>
            )}

            {/* Main Content */}
            {loading ? (
                <div className="store-admin-loading">
                    <div className="store-admin-spinner"></div>
                    <p>Завантаження даних...</p>
                </div>
            ) : activeTab === 'inventory' ? (
                items.length === 0 ? (
                    <div className="store-admin-empty">
                        <ShoppingBag size={48} />
                        <p>У магазині ще немає товарів. Натисніть "Додати товар" для наповнення.</p>
                    </div>
                ) : (
                    <div className="inventory-grid">
                        {items.map((item) => (
                            <div key={item._id} className={`inventory-card ${!item.isActive ? 'inactive' : ''}`}>
                                {item.imageUrl ? (
                                    <div className="inventory-card-img" style={{ backgroundImage: `url(${resolveAssetUrl(item.imageUrl)})` }} />
                                ) : (
                                    <div className="inventory-card-img-placeholder">
                                        <ShoppingBag size={32} />
                                    </div>
                                )}
                                <div className="inventory-card-content">
                                    <div className="inventory-card-title-row">
                                        <h3>{item.title}</h3>
                                        <span className={`status-pill ${item.isActive ? 'active' : 'inactive'}`}>
                                            {item.isActive ? 'Активний' : 'Чернетка'}
                                        </span>
                                    </div>
                                    <p className="inventory-card-desc">{item.description || 'Опис відсутній'}</p>
                                    
                                    <div className="inventory-card-details">
                                        <div>Ціна: <strong>🪙 {item.price} Coins</strong></div>
                                        <div>Склад: <strong>{item.stock === -1 ? 'Безліміт' : `${item.stock} шт.`}</strong></div>
                                        <div className="city-detail">
                                            {item.city ? (
                                                <>
                                                    <MapPin size={12} />
                                                    <span>{item.city}</span>
                                                </>
                                            ) : (
                                                <>
                                                    <Globe size={12} />
                                                    <span>Для всіх міст</span>
                                                </>
                                            )}
                                        </div>
                                    </div>
                                    
                                    <div className="inventory-card-actions">
                                        <button className="btn-edit-item" onClick={() => handleOpenItemModal(item)}>
                                            <Edit size={14} />
                                            <span>Редагувати</span>
                                        </button>
                                        <button className="btn-delete-item" onClick={() => handleDeleteItem(item._id)}>
                                            <Trash2 size={14} />
                                        </button>
                                    </div>
                                </div>
                            </div>
                        ))}
                    </div>
                )
            ) : (
                filteredPurchases.length === 0 ? (
                    <div className="store-admin-empty">
                        <ShoppingBag size={48} />
                        <p>Немає замовлень у цій категорії</p>
                    </div>
                ) : (
                    <div className="admin-orders-list">
                        {filteredPurchases.map((pur) => (
                            <div key={pur._id} className={`admin-order-card border-${pur.status}`}>
                                <div className="order-main-info">
                                    <div className="order-student-info">
                                        <h4>{pur.studentId ? `${pur.studentId.studentName} ${pur.studentId.studentLastName}` : 'Невідомий гравець'}</h4>
                                        <div className="student-badges">
                                            {pur.studentId?.studentCity && (
                                                <span className="info-badge"><MapPin size={10} /> {pur.studentId.studentCity}</span>
                                            )}
                                            {pur.studentId?.position && (
                                                <span className="info-badge">💼 {pur.studentId.position}</span>
                                            )}
                                        </div>
                                    </div>
                                    
                                    <div className="order-product-info">
                                        <span className="product-label">Товар:</span>
                                        <span className="product-title">{pur.itemId?.title || 'Видалений товар'}</span>
                                    </div>

                                    {pur.notes && (
                                        <div className="order-notes">
                                            <strong>Коментар гравця:</strong>
                                            <p>"{pur.notes}"</p>
                                        </div>
                                    )}

                                    <div className="order-date">
                                        Замовлено: {new Date(pur.createdAt).toLocaleDateString('uk-UA')} {new Date(pur.createdAt).toLocaleTimeString('uk-UA', { hour: '2-digit', minute: '2-digit' })}
                                    </div>
                                </div>

                                <div className="order-status-actions">
                                    <div className="order-price">
                                        <span>🪙 {pur.coinsSpent} Coins</span>
                                    </div>
                                    
                                    {pur.status === 'pending' ? (
                                        <div className="order-action-buttons">
                                            <button 
                                                className="btn-order-complete" 
                                                onClick={() => handleUpdatePurchaseStatus(pur._id, 'completed')}
                                                title="Видати товар"
                                            >
                                                <Check size={16} />
                                                <span>Видати</span>
                                            </button>
                                            <button 
                                                className="btn-order-cancel" 
                                                onClick={() => handleUpdatePurchaseStatus(pur._id, 'canceled')}
                                                title="Скасувати та повернути монети"
                                            >
                                                <X size={16} />
                                                <span>Скасувати</span>
                                            </button>
                                        </div>
                                    ) : (
                                        <div className="order-status-badge">
                                            {pur.status === 'completed' ? (
                                                <span className="status-badge-text text-completed"><Check size={14} /> Видано</span>
                                            ) : (
                                                <span className="status-badge-text text-canceled"><X size={14} /> Скасовано / Повернено</span>
                                            )}
                                        </div>
                                    )}
                                </div>
                            </div>
                        ))}
                    </div>
                )
            )}

            {/* Create / Edit Store Item Modal */}
            {isItemModalOpen && (
                <div className="item-modal-overlay" onClick={handleCloseItemModal}>
                    <div className="item-modal" onClick={(e) => e.stopPropagation()}>
                        <div className="item-modal-header">
                            <h3>{editingItem ? 'Редагувати товар' : 'Додати новий товар'}</h3>
                            <button className="item-modal-close" onClick={handleCloseItemModal} disabled={submittingForm}>&times;</button>
                        </div>
                        <form onSubmit={handleSubmitItemForm}>
                            <div className="item-modal-body">
                                <div className="form-group">
                                    <label htmlFor="title">Назва товару *</label>
                                    <input 
                                        type="text" 
                                        id="title" 
                                        name="title" 
                                        value={itemForm.title} 
                                        onChange={handleFormChange}
                                        required 
                                        disabled={submittingForm}
                                    />
                                </div>
                                
                                <div className="form-group">
                                    <label htmlFor="description">Опис товару</label>
                                    <textarea 
                                        id="description" 
                                        name="description" 
                                        value={itemForm.description} 
                                        onChange={handleFormChange}
                                        disabled={submittingForm}
                                        rows={3}
                                    />
                                </div>

                                <div className="form-row">
                                    <div className="form-group half">
                                        <label htmlFor="price">Вартість (Coins) *</label>
                                        <input 
                                            type="number" 
                                            id="price" 
                                            name="price" 
                                            value={itemForm.price} 
                                            onChange={handleFormChange}
                                            required 
                                            min={0}
                                            disabled={submittingForm}
                                        />
                                    </div>
                                    <div className="form-group half">
                                        <label htmlFor="stock">Кількість на складі (-1 = безліміт)</label>
                                        <input 
                                            type="number" 
                                            id="stock" 
                                            name="stock" 
                                            value={itemForm.stock} 
                                            onChange={handleFormChange}
                                            min={-1}
                                            disabled={submittingForm}
                                        />
                                    </div>
                                </div>

                                <div className="form-group">
                                    <label htmlFor="city">Обмеження по місту (порожньо = доступно всім)</label>
                                    <select 
                                        id="city" 
                                        name="city" 
                                        value={itemForm.city} 
                                        onChange={handleFormChange}
                                        disabled={submittingForm || user?.role !== 'superadmin'}
                                    >
                                        <option value="">-- Всі міста --</option>
                                        {cities.map(c => (
                                            <option key={c._id} value={c.name}>{c.name}</option>
                                        ))}
                                    </select>
                                    {user?.role !== 'superadmin' && (
                                        <span className="field-hint">Тільки суперадміністратор може змінювати місто товарів</span>
                                    )}
                                </div>

                                <div className="form-group">
                                    <label>Зображення товару</label>
                                    <div className="image-upload-wrapper">
                                        {itemForm.imageUrl && (
                                            <div className="uploaded-image-preview">
                                                <img src={resolveAssetUrl(itemForm.imageUrl)} alt="Preview" />
                                                <button 
                                                    type="button" 
                                                    className="btn-remove-image"
                                                    onClick={() => setItemForm(prev => ({ ...prev, imageUrl: '' }))}
                                                    disabled={submittingForm}
                                                >
                                                    Видалити
                                                </button>
                                            </div>
                                        )}
                                        <label className="image-upload-btn-label">
                                            <Upload size={16} />
                                            <span>{uploadingImage ? 'Завантаження...' : 'Завантажити фото'}</span>
                                            <input 
                                                type="file" 
                                                accept="image/*" 
                                                onChange={handleImageUpload} 
                                                disabled={uploadingImage || submittingForm}
                                                style={{ display: 'none' }}
                                            />
                                        </label>
                                    </div>
                                </div>

                                <div className="form-group checkbox-group">
                                    <input 
                                        type="checkbox" 
                                        id="isActive" 
                                        name="isActive" 
                                        checked={itemForm.isActive} 
                                        onChange={handleFormChange}
                                        disabled={submittingForm}
                                    />
                                    <label htmlFor="isActive">Показувати товар на вітрині (активний)</label>
                                </div>
                            </div>
                            <div className="item-modal-footer">
                                <button 
                                    type="button" 
                                    className="btn-modal-cancel" 
                                    onClick={handleCloseItemModal}
                                    disabled={submittingForm}
                                >
                                    Скасувати
                                </button>
                                <button 
                                    type="submit" 
                                    className="btn-modal-submit"
                                    disabled={submittingForm}
                                >
                                    {submittingForm ? 'Збереження...' : editingItem ? 'Оновити' : 'Створити'}
                                </button>
                            </div>
                        </form>
                    </div>
                </div>
            )}
        </div>
    );
};

export default StoreManager;
